import type { TaskModalTask } from "@common/types";
import type { ConsumedTaskItem } from "@common/types/Inventory";
import { Info } from "lucide-react";

import { Empty, EmptyDescription, EmptyHeader, EmptyMedia } from "components/core/Empty";
import { Flex } from "components/core/Flex";
import { PaginationControl } from "components/core/Pagination";
import { Spinner } from "components/core/Spinner";
import { Text } from "components/core/Text";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/core/Tooltip";
import EmptySearchIcon from "images/empty-search.svg?react";
import useTaskItemsQuery from "queries/useTaskItemsQuery";

import ItemRow from "./ItemRow";

type Props = {
  currentPage: number;
  onChange: (item: ConsumedTaskItem) => void;
  setCurrentPage: (page: number) => void;
  showOnlyTaskGardenItems: boolean;
  task: Pick<TaskModalTask, "id">;
};

const STALE_TIME = 3000;

const MissingItemsTooltip = () => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Text className="cursor-pointer" color="subtle" variant="caption">
        <Info size={12} /> Why am I not seeing items?
      </Text>
    </TooltipTrigger>
    <TooltipContent>
      Items appear here when their task type and crop match this task. Check that your
      inventory items are assigned to the correct task type and crop in the Inventory
      page.
    </TooltipContent>
  </Tooltip>
);

const InventoryList = ({
  showOnlyTaskGardenItems,
  task,
  currentPage,
  onChange,
  setCurrentPage
}: Props) => {
  const { data: taskItems, isFetching } = useTaskItemsQuery({
    showOnlyTaskGardenItems,
    taskId: task.id,
    page: currentPage,
    // We do this query right before the modal is opened, so we can afford to be stale for a bit
    staleTime: STALE_TIME
  });

  if (isFetching || !taskItems) {
    return (
      <Flex justify="center">
        <Spinner />
      </Flex>
    );
  }

  return (
    <Flex align="center" direction="col" gap={4}>
      {taskItems.items.length === 0 ? (
        <Empty style={{ maxWidth: 300 }}>
          <EmptyHeader>
            <EmptyMedia>
              <EmptySearchIcon />
            </EmptyMedia>
            <EmptyDescription>
              <Flex align="center" direction="col" gap={1}>
                <span>No inventory items found for this task.</span>
                <MissingItemsTooltip />
              </Flex>
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <>
          {taskItems.items.map((item) => (
            <ItemRow key={item.id} item={item} onChange={onChange} />
          ))}
        </>
      )}
      <PaginationControl
        hideOnSinglePage
        current={currentPage}
        total={taskItems.pagination.count}
        onChange={setCurrentPage}
      />
    </Flex>
  );
};

export default InventoryList;
